import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import moment from 'moment'
import API from '../../../services/API'
import { UserContext } from '../../../App'
import BigModal from '../../../components/Modals/BigModal'
import AppraisalModalHeader from './AppraisalModalHeader'
import PreAppraisalForm from './PreAppraisalForm'
import EmployeeSectionForm from './EmployeeSectionForm'
import OccupationalHealthStep from './OccupationalHealthStep'

const statusColors = {
  'Awaiting Employee': 'contrast',
  'Awaiting HR Review': 'info',
  Completed: 'success',
  Cancelled: 'default',
}

// The occupational health screening is confirmed/updated as the first step
// of each appraisal: it needs doing when the last screening predates the
// appraisal record
const needsOHFor = (appraisal, ohStatus) =>
  !!appraisal &&
  ohStatus?.enabled !== false &&
  (!ohStatus?.lastSubmittedAt ||
    moment(ohStatus.lastSubmittedAt).isBefore(appraisal.createdAt))

const nextStepFor = (appraisal, ohStatus) => {
  if (!appraisal || appraisal.status !== 'Awaiting Employee') return null
  if (needsOHFor(appraisal, ohStatus)) return 'occupational-health'
  if (!appraisal.preAppraisal?.completedAt) return 'pre-appraisal'
  if (!appraisal.employeeSection?.completedAt) return 'employee-section'
  return null
}

const MyAppraisals = () => {
  const [loading, setLoading] = useState(true)
  const [appraisals, setAppraisals] = useState([])
  const [appraisalsEnabled, setAppraisalsEnabled] = useState(false)
  const [selectedAppraisal, setSelectedAppraisal] = useState(null)
  const [ohStatus, setOhStatus] = useState(null)
  const { user } = useContext(UserContext)

  const loadAppraisals = () => {
    const employeeID = user?.employee?._id
    if (!employeeID) return
    setLoading(true)
    Promise.all([
      API.get(`/appraisals/employee/${employeeID}`),
      API.get('/settings?select=staffAppraisalSettings.enabled'),
      API.get('/occupational-health/my-status'),
    ])
      .then(([appraisalsRes, settingsRes, ohRes]) => {
        const enabled =
          settingsRes.status === 200 &&
          settingsRes.data?.staffAppraisalSettings?.enabled !== false
        setAppraisalsEnabled(enabled)
        if (ohRes.status === 200) setOhStatus(ohRes.data)
        if (appraisalsRes.status === 200) {
          setAppraisals(
            enabled
              ? appraisalsRes.data
              : appraisalsRes.data.filter(
                  (appraisal) => appraisal.status !== 'Awaiting Employee',
                ),
          )
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadAppraisals()
  }, [user?.employee?._id])

  const handlePreAppraisalSaved = (updatedAppraisal) => {
    setSelectedAppraisal(updatedAppraisal)
    loadAppraisals()
  }

  const handleEmployeeSectionSaved = () => {
    loadAppraisals()
    setSelectedAppraisal(null)
  }

  const currentStep = nextStepFor(selectedAppraisal, ohStatus)

  const steps = [
    ...(ohStatus?.enabled !== false
      ? [
          {
            label: 'Health Screening',
            status:
              currentStep === 'occupational-health'
                ? 'current'
                : selectedAppraisal &&
                    !needsOHFor(selectedAppraisal, ohStatus)
                  ? 'done'
                  : 'upcoming',
          },
        ]
      : []),
    {
      label: 'Pre-Appraisal',
      status:
        currentStep === 'pre-appraisal'
          ? 'current'
          : selectedAppraisal?.preAppraisal?.completedAt
            ? 'done'
            : 'upcoming',
    },
    {
      label: 'Self-Review',
      status:
        currentStep === 'employee-section'
          ? 'current'
          : selectedAppraisal?.employeeSection?.completedAt
            ? 'done'
            : 'upcoming',
    },
  ]

  return (
    <Stack gap={2}>
      <Typography variant="title2">My Appraisals</Typography>
      {!loading && !appraisalsEnabled && (
        <Alert severity="info">
          Staff appraisals are currently disabled. Appraisals already submitted
          for HR review remain visible here.
        </Alert>
      )}
      {loading ? (
        <LinearProgress />
      ) : (
        <Stack gap={1}>
          {appraisals.length === 0 && (
            <Typography color="text.secondary">
              You have no appraisals yet.
            </Typography>
          )}
          {appraisals.map((appraisal) => {
            const nextStep = nextStepFor(appraisal, ohStatus)
            return (
              <Card
                key={appraisal._id}
                variant="outlined"
                sx={{ cursor: nextStep ? 'pointer' : 'default' }}
                onClick={() => nextStep && setSelectedAppraisal(appraisal)}
              >
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Stack>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Appraisal due{' '}
                        {moment(appraisal.dueDate).format('Do MMM YYYY')}
                      </Typography>
                      {nextStep && (
                        <Typography variant="body2" color="text.secondary">
                          Action needed:{' '}
                          {nextStep === 'occupational-health'
                            ? 'Confirm your occupational health details'
                            : nextStep === 'pre-appraisal'
                              ? 'Complete your pre-appraisal'
                              : 'Complete your self-review'}
                        </Typography>
                      )}
                    </Stack>
                    <Chip
                      label={appraisal.status}
                      color={statusColors[appraisal.status] ?? 'default'}
                    />
                  </Stack>
                </CardContent>
              </Card>
            )
          })}
        </Stack>
      )}

      <BigModal
        open={!!selectedAppraisal}
        onClose={() => setSelectedAppraisal(null)}
        style={{ padding: 0 }}
      >
        {selectedAppraisal && (
          <Box>
            <AppraisalModalHeader
              title="Your Appraisal"
              subtitle="Complete the steps below so your appraisal is ready for HR review."
              steps={steps}
            />
            <Box px={{ xs: 1, md: 1.5 }} pb={1}>
              {currentStep === 'occupational-health' ? (
                <OccupationalHealthStep
                  employeeID={user?.employee?._id}
                  onConfirmed={loadAppraisals}
                />
              ) : currentStep === 'pre-appraisal' ? (
                <PreAppraisalForm
                  appraisal={selectedAppraisal}
                  onSaved={handlePreAppraisalSaved}
                />
              ) : (
                <EmployeeSectionForm
                  appraisal={selectedAppraisal}
                  onSaved={handleEmployeeSectionSaved}
                />
              )}
            </Box>
          </Box>
        )}
      </BigModal>
    </Stack>
  )
}

export default MyAppraisals
